import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, InputAdornment, MenuItem, TextField, useMediaQuery } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import JobsTable from '../components/jobs/JobsTable';
import JobFormDrawer from '../components/jobs/JobFormDrawer';
import { EmptyState, FilterPill } from '../components/common/ui';
import JobService from '../services/JobService';
import UserService from '../services/UserService';
import CategoryService from '../services/CategoryService';
import { STATUS, STATUS_ORDER } from '../styles/tokens';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageRefresh } from '../components/layout/pullToRefresh';

const STAGES = ['FIELD_PENDING', 'AWAITING_SUBMISSION', 'AT_CADASTRE'];

export default function JobsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { isAdmin } = useAuth();
    const [params, setParams] = useSearchParams();
    const [searchText, setSearchText] = useState(params.get('q') || '');
    const [q, setQ] = useState(params.get('q') || '');
    const [statuses, setStatuses] = useState([]);
    const [categoryId, setCategoryId] = useState(() => (params.get('categoryId') ? Number(params.get('categoryId')) : ''));
    const [paid, setPaid] = useState('');
    const [sort, setSort] = useState('created');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Work stage (FIELD_PENDING | AWAITING_SUBMISSION | AT_CADASTRE), person, municipality, cadastral zone.
    const [stage, setStage] = useState(params.get('stage') || '');
    const [assignee, setAssignee] = useState('');
    const [municipality, setMunicipality] = useState(null);
    const [zone, setZone] = useState(null);
    const [team, setTeam] = useState([]);
    const [values, setValues] = useState({ municipalities: [], zones: [] });

    // Top-bar search navigates here with ?q=...; keep the field in sync.
    useEffect(() => {
        const urlQ = params.get('q') || '';
        setSearchText(urlQ);
        setQ(urlQ);
        if (params.get('stage')) setStage(params.get('stage'));
        if (params.get('categoryId')) setCategoryId(Number(params.get('categoryId')));
        setPage(0);
    }, [params]);

    useEffect(() => {
        const h = setTimeout(() => {
            if (searchText !== q) { setQ(searchText); setPage(0); }
        }, 300);
        return () => clearTimeout(h);
    }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        CategoryService.list().then(setCategories).catch(() => {});
        UserService.directory().then(setTeam).catch(() => {});
        JobService.surveyValues().then(setValues).catch(() => {});
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        return JobService.list({
            q: q || undefined,
            status: statuses.length ? statuses : undefined,
            categoryId: categoryId || undefined,
            paid: paid === '' ? undefined : paid,
            stage: stage || undefined,
            assignee: assignee || undefined,
            municipality: municipality || undefined,
            zone: zone || undefined,
            sort, page, size,
        })
            .then(setData)
            .catch(() => showToast(t('common.loadError'), 'error'))
            .finally(() => setLoading(false));
    }, [q, statuses, categoryId, paid, stage, assignee, municipality, zone, sort, page, size, showToast, t]);

    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    const toggleStatus = (s) => {
        setPage(0);
        setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    };

    const filtersActive = q || statuses.length || categoryId || paid !== '' || stage || assignee || municipality || zone;
    const clearFilters = () => {
        setStatuses([]); setCategoryId(''); setPaid(''); setSearchText(''); setQ(''); setPage(0);
        setStage(''); setAssignee(''); setMunicipality(null); setZone(null);
        if (params.get('q') || params.get('stage') || params.get('categoryId')) setParams({});
    };
    const pick = (setter) => (v) => { setter(v); setPage(0); };
    // Phones: the filters (7 controls) sit behind a button so the list is not pushed off the screen.
    const phone = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [showFilters, setShowFilters] = useState(false);
    const activeCount = [statuses.length, categoryId, paid !== '', stage, assignee, municipality, zone].filter(Boolean).length;

    return (
        <Layout>
            <PageHeader
                title={t('jobs.title')}
                count={data.total || null}
                action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDrawerOpen(true)}>{t('jobs.new')}</Button>}
            />

            <Box sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '14px', p: 1.5, mb: 2.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
                <TextField size="small" placeholder={t('jobs.searchPlaceholder')} value={searchText} onChange={(e) => setSearchText(e.target.value)}
                           sx={{ flex: '1 1 260px', minWidth: 220 }}
                           slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment> } }} />
                {phone && (
                    <Button variant={activeCount ? 'contained' : 'outlined'} disableElevation startIcon={<TuneIcon />} onClick={() => setShowFilters(v => !v)}>
                        {t('jobs.filters.title')}{activeCount ? ` (${activeCount})` : ''}
                    </Button>
                )}
                {(!phone || showFilters) && <>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <FilterPill label={t('common.all')} active={statuses.length === 0} onClick={() => { setStatuses([]); setPage(0); }} />
                    {STATUS_ORDER.map(s => (
                        <FilterPill key={s} label={t(`status.${s}`)} color={STATUS[s].color} active={statuses.includes(s)} onClick={() => toggleStatus(s)} />
                    ))}
                </Box>
                <TextField select size="small" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(0); }}
                           sx={{ minWidth: 170 }} slotProps={{ select: { displayEmpty: true } }}>
                    <MenuItem value="">{t('jobs.filters.allCategories')}</MenuItem>
                    {categories.map(c => (
                        <MenuItem key={c.id} value={c.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: c.color }} />{c.name}</Box>
                        </MenuItem>
                    ))}
                </TextField>
                {isAdmin && (
                    <TextField select size="small" value={paid} onChange={(e) => { setPaid(e.target.value); setPage(0); }}
                               sx={{ minWidth: 140 }} slotProps={{ select: { displayEmpty: true } }}>
                        <MenuItem value="">{t('jobs.filters.anyPayment')}</MenuItem>
                        <MenuItem value="true">{t('jobs.paid')}</MenuItem>
                        <MenuItem value="false">{t('jobs.unpaid')}</MenuItem>
                    </TextField>
                )}
                <TextField select size="small" value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}
                           label={t('jobs.filters.sort')}>
                    <MenuItem value="created">{t('jobs.filters.sortNewest')}</MenuItem>
                    <MenuItem value="scheduled">{t('jobs.filters.sortScheduled')}</MenuItem>
                    <MenuItem value="number">{t('jobs.filters.sortNumber')}</MenuItem>
                </TextField>
                {filtersActive ? <Button size="small" onClick={clearFilters} sx={{ color: '#64748B' }}>{t('common.clearFilters')}</Button> : null}
                {/* Second row: where the work stands, who does it, where it is. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, flexBasis: '100%' }}>
                    <TextField select size="small" value={stage} onChange={(e) => pick(setStage)(e.target.value)}
                               sx={{ minWidth: 250, flex: { xs: '1 1 100%', sm: '0 1 auto' } }} slotProps={{ select: { displayEmpty: true } }}>
                        <MenuItem value="">{t('jobs.filters.anyStage')}</MenuItem>
                        {STAGES.map(s => <MenuItem key={s} value={s}>{t(`jobs.stage.${s}`)}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" value={assignee} onChange={(e) => pick(setAssignee)(e.target.value)}
                               sx={{ minWidth: 170, flex: { xs: '1 1 100%', sm: '0 1 auto' } }} slotProps={{ select: { displayEmpty: true } }}>
                        <MenuItem value="">{t('jobs.filters.anyone')}</MenuItem>
                        {team.map(u => <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>)}
                    </TextField>
                    <Autocomplete size="small" options={values.municipalities} value={municipality} onChange={(_, v) => pick(setMunicipality)(v)}
                                  sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                                  renderInput={(p) => <TextField {...p} placeholder={t('jobs.fields.municipality')} />} />
                    <Autocomplete size="small" options={values.zones} value={zone} onChange={(_, v) => pick(setZone)(v)}
                                  sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                                  renderInput={(p) => <TextField {...p} placeholder={t('jobs.fields.cadastralZone')} />} />
                </Box>
                </>}
            </Box>

            {loading && data.items.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : data.items.length === 0 ? (
                filtersActive
                    ? <EmptyState icon={<SearchIcon />} title={t('jobs.emptyFiltered')} text={t('jobs.emptyFilteredHint')}
                                  action={<Button onClick={clearFilters}>{t('common.clearFilters')}</Button>} />
                    : <EmptyState icon={<WorkOutlineIcon />} title={t('jobs.empty')} text={t('jobs.emptyHint')}
                                  action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDrawerOpen(true)}>{t('jobs.new')}</Button>} />
            ) : (
                <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                    <JobsTable jobs={data.items} total={data.total} page={page} size={size}
                               onPage={setPage} onSize={(s) => { setSize(s); setPage(0); }} />
                </Box>
            )}

            <JobFormDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
                           onSaved={(job) => { setDrawerOpen(false); navigate(`/jobs/${job.id}`); }} />
        </Layout>
    );
}
